/**
 * assessmentApi — thin axios wrappers for /api/assessment/*.
 *
 * All methods derive person_id server-side from req.user.id, so the
 * client never has to pass a user id. domainId and roleSpecId are
 * required inputs from the caller (learner picks a role/domain in the
 * page shell).
 */

import api from './api';

const assessmentApi = {
  /**
   * Next assessment task for the learner in a domain (+ optional role).
   * Server returns 204 when nothing is available — axios surfaces as
   * response.status === 204 with data === '' (or undefined).
   */
  async getNextTask({ domainId, roleSpecId }) {
    const params = { domainId };
    if (roleSpecId) params.roleSpecId = roleSpecId;
    const res = await api.get('/api/assessment/tasks/next', { params });
    if (res.status === 204) return null;
    return res.data && res.data.task ? res.data.task : null;
  },

  /**
   * Submit an answer for a task. Server routes through the grader,
   * validates verbatim, and inserts a capability_evidence row —
   * either auto-confirmed (levels 0-2) or awaiting review (3-4) or
   * rejected (verbatim / contract fail).
   */
  async submitAnswer({ taskId, answer }) {
    const res = await api.post(`/api/assessment/tasks/${taskId}/submit`, { answer });
    return res.data;
  },

  /**
   * My skill profile for a domain — one row per active capability,
   * left-joined against skill_profile so unconfirmed capabilities
   * arrive as current_level=0.
   */
  async getProfile({ domainId }) {
    const res = await api.get('/api/assessment/profile', { params: { domainId } });
    return res.data && res.data.profile ? res.data.profile : [];
  },

  /**
   * Latest roadmap for me + role. Server returns 204 if I've never
   * generated one; the client treats null as "generate to start".
   */
  async getRoadmap({ roleSpecId }) {
    const res = await api.get('/api/assessment/roadmap', { params: { roleSpecId } });
    if (res.status === 204) return null;
    return res.data && res.data.roadmap ? res.data.roadmap : null;
  },

  /**
   * Regenerate roadmap — creates a new version, does NOT touch old
   * versions. Server returns { roadmap_id, version, item_count }.
   */
  async regenerateRoadmap({ domainId, roleSpecId, goalId = null }) {
    const body = { domainId, roleSpecId };
    if (goalId) body.goalId = goalId;
    const res = await api.post('/api/assessment/roadmap', body);
    return res.data;
  },
};

export default assessmentApi;
